/**
 * @file Invoice.datasource.ts
 * @description Datasource utilities for Invoice validation
 * @module Invoices/Invoice
 *
 * This file contains type definitions and utilities for form datasources
 * extracted from Entity component to avoid circular dependencies in tests.
 */

/**
 * Type representing form datasource data structure
 */
export type FormDatasourceData = { [fieldName: string]: any };

/**
 * Parses form datasource data into a more accessible format
 *
 * Converts datasource array structure into an object where:
 * - Keys are field names
 * - Values are objects mapping internalCode to value
 *
 * @param formDatasourceData - Raw datasource data
 * @returns Parsed datasource object or null if empty
 *
 * @example
 * const formData = {
 *   listPaymentOptions: [
 *     { internalCode: 'FREIGHT_CLAIM', value: 5 },
 *     { internalCode: 'COST_OF_JOB', value: 1 }
 *   ]
 * };
 *
 * const parsed = parseFormDatasourceData(formData);
 * // Returns: { listPaymentOptions: { FREIGHT_CLAIM: 5, COST_OF_JOB: 1 } }
 */
export const parseFormDatasourceData = (formDatasourceData: FormDatasourceData | null) => {
  const parsedFormDatasourceData: {
    [fieldName: string]: { [internalCode: string]: string };
  } = {}; // string is the label associated with the value from the datasource data

  if (formDatasourceData) {
    Object.keys(formDatasourceData).forEach((key) => {
      const value = formDatasourceData[key];
      let parsedValue: any = {};

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item.internalCode && typeof item.internalCode === "string" && item.value) {
            parsedValue[item.internalCode] = item.value;
          }
        });
      }

      parsedFormDatasourceData[key] = parsedValue;
    });
  }

  if (Object.keys(parsedFormDatasourceData).length > 0) {
    return parsedFormDatasourceData;
  } else {
    return null;
  }
};
